import { createContext, useContext, useState, ReactNode, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import ConfirmSendModal from './documents/confirm-send-modal'
import {
  markSent,
  markPrepFailed,
  markSendFailed,
  prepareDelivery,
  loadDeliveriesBySubTrades,
} from 'app/state/ducks/trade-document-delivery/thunks'
import { generateOpenTradePacketEml } from './open-trade-packet-generator'
import { loadDeliveries } from 'app/state/ducks/trade-document-delivery/actions'
import { PacketType } from 'app/state/ducks/trade-document-delivery/types'
import { packetDisplayName } from './documents/delivery-view'
import { SuccessNotification, ErrorNotification } from 'app/views/components/notifications/notification'

// LOS-6471 popup modes:
//  * send      — standard Confirm Send (Prepared -> Sent)
//  * resend    — stale Open packet advisory; Re-send returns it to Prepared
//  * retryPrep — a failed preparation; Retry returns it to Prepared
//  * retrySend — a failed confirm-send; Retry returns it to Prepared
export type ConfirmMode = 'send' | 'resend' | 'retryPrep' | 'retrySend'

// Accepts one sub-trade (icon click) or the whole selection (after generating
// an Open Trade Packet, whose single email covers N sub-trades).
type Ctx = {
  openConfirm: (subTradeIds: string | string[], packetType?: PacketType, mode?: ConfirmMode) => void
}
const DeliveryConfirmContext = createContext<Ctx>({ openConfirm: () => {} })
export const useDeliveryConfirm = () => useContext(DeliveryConfirmContext)

const modalCopy = (mode: ConfirmMode, packetName: string) => {
  switch (mode) {
    case 'resend':
      return {
        title: 'Re-send Packet',
        body: 'AA economics have changed since this packet was sent. Re-sending will mark it as Prepared — confirm delivery once the email is sent.',
        confirmLabel: 'Re-send',
      }
    case 'retryPrep':
      return {
        title: 'Retry Preparation',
        body: `Preparing the ${packetName} failed. Retry will mark it as Prepared — regenerate the email, then confirm delivery once it is sent.`,
        confirmLabel: 'Retry',
      }
    case 'retrySend':
      return {
        title: 'Retry Send',
        body: `Confirming the ${packetName} send failed. Retry will return it to Prepared — confirm delivery again once the email is sent.`,
        confirmLabel: 'Retry',
      }
    default:
      return {
        title: undefined,
        body: `Confirm the ${packetName} was sent. This will mark it as Sent.`,
        confirmLabel: 'Confirm Send',
      }
  }
}

export function DeliveryConfirmProvider({
  masterTradeId,
  subTradeIds,
  children,
}: {
  masterTradeId: string
  subTradeIds: string[]
  children: ReactNode
}) {
  const dispatch = useDispatch()
  const [confirm, setConfirm] = useState<{
    ids: string[]
    packetType: PacketType
    mode: ConfirmMode
  } | null>(null)

  const reload = useCallback(() => {
    dispatch(loadDeliveriesBySubTrades(subTradeIds) as any)
  }, [dispatch, subTradeIds])

  const openConfirm = useCallback(
    (ids: string | string[], packetType: PacketType = 'settled', mode: ConfirmMode = 'send') => {
      const list = (Array.isArray(ids) ? ids : [ids]).filter(Boolean)
      if (list.length) setConfirm({ ids: list, packetType, mode })
    },
    [],
  )

  const onConfirm = async () => {
    if (!confirm) return
    if (confirm.mode === 'send') {
      const res: any = await markSent(masterTradeId, confirm.ids, confirm.packetType)
      if (res.success) {
        SuccessNotification({ title: 'Marked sent', message: 'Delivery marked as sent.' })
        // Apply the authoritative record returned by the endpoint (status + sentAt).
        dispatch(loadDeliveries(res.payload ?? []))
      } else {
        ErrorNotification({ title: 'Mark sent failed', message: String(res.payload) })
        // Record the outcome so the icon renders Send Failed (LOS-6471).
        const failed: any = await markSendFailed(masterTradeId, confirm.ids, confirm.packetType)
        if (failed.success) dispatch(loadDeliveries(failed.payload ?? []))
      }
      return
    }
    // Re-send of a stale Open packet regenerates and downloads a FRESH .eml
    // (post-change documents) before re-preparing — one click does both.
    if (confirm.mode === 'resend' && confirm.packetType === 'open') {
      try {
        const gen = await generateOpenTradePacketEml(confirm.ids)
        if (!gen.ok) {
          ErrorNotification({
            title: gen.blocked ? 'Cannot re-send Open Trade Packet' : 'Open Trade Packet failed',
            message: gen.message,
          })
          // This is the entry point the disabled menu item does NOT cover — a
          // stale re-send is launched from the Delivery icon — so the backend
          // block surfaces here instead (LOS-6934). Recording Prep Failed would
          // be worse than noise on this path: an existing Prepared record is
          // DOWNGRADED to PrepFailed, losing state the user still needs.
          if (!gen.blocked) {
            const failed: any = await markPrepFailed(masterTradeId, confirm.ids, 'open')
            if (failed.success) dispatch(loadDeliveries(failed.payload ?? []))
          }
          return
        }
      } catch (e: any) {
        ErrorNotification({
          title: 'Open Trade Packet failed',
          message: e?.message || 'Failed to build the Open Trade Packet',
        })
        const failed: any = await markPrepFailed(masterTradeId, confirm.ids, 'open')
        if (failed.success) dispatch(loadDeliveries(failed.payload ?? []))
        return
      }
    }
    // resend / retryPrep / retrySend all re-prepare (ADR_LOS-027): the record
    // returns to Prepared, sentAt clears (self-clearing the derived staleness),
    // and the user then confirms the send via the standard blue-icon flow.
    const res: any = await prepareDelivery(masterTradeId, confirm.ids, confirm.packetType)
    if (res.success) {
      SuccessNotification({
        title: confirm.mode === 'resend' ? 'Packet re-prepared' : 'Ready to retry',
        message:
          confirm.mode === 'resend' && confirm.packetType === 'open'
            ? 'A fresh email was downloaded and the packet is Prepared — send it, then confirm delivery.'
            : 'The packet is Prepared — send the email, then confirm delivery.',
      })
      dispatch(loadDeliveries(res.payload ?? []))
    } else {
      ErrorNotification({ title: 'Re-prepare failed', message: String(res.payload) })
    }
  }

  const onClose = () => {
    setConfirm(null)
    // If user cancels after preparing EML, force a refresh so "Prepared" state appears.
    reload()
  }

  const copy = confirm ? modalCopy(confirm.mode, packetDisplayName(confirm.packetType)) : null

  return (
    <DeliveryConfirmContext.Provider value={{ openConfirm }}>
      {children}
      <ConfirmSendModal
        opened={confirm !== null}
        onClose={onClose}
        onConfirm={onConfirm}
        packetName={confirm ? packetDisplayName(confirm.packetType) : undefined}
        title={copy?.title}
        body={copy?.body}
        confirmLabel={copy?.confirmLabel}
      />
    </DeliveryConfirmContext.Provider>
  )
}
